import { border, lodestarColors, space, typography, ursaColors } from "@smartsheet/design-tokens";
import { color } from "@smartsheet/design-tokens-react";
import { toRgba } from "../../../../constants/colors";
import { shadows } from "../../../../constants/layers";
import { sizes } from "../../../../constants/spacing";
import { RequiredThemeKeys, themed } from "../../../../util/theme";
import { DatePickerDayBoxProps } from "./DatePickerDayBox";
import { DatePickerDayButtonStyledProps } from "./DatePickerDayButtonStyled";
import { DatePickerDialogStyledProps } from "./DatePickerDialogStyled";

export const datePickerTheme = themed({
    backgroundColor: {
        "lodestar-light": lodestarColors.neutralLight40,
        "ursa-light": color("surface--work--background"),
    },
    boxShadow: {
        "lodestar-light": shadows.context(),
        "ursa-light": color("elevation--shadow-2"),
    },
    borderRadius: {
        "lodestar-light": `${sizes.xxSmall}px`,
        "ursa-light": space.space20,
    },
    color: {
        "lodestar-light": lodestarColors.neutralDark40,
        "ursa-light": color("surface--work--text"),
    },
    dayContainerMarginTop: {
        "lodestar-light": "18px",
        "ursa-light": space.space30,
    },
    footer: {
        marginTop: {
            "lodestar-light": space.space60,
            "ursa-light": space.space30,
        },
        buttonAppearance: {
            apply: {
                "lodestar-light": "primary",
                "ursa-light": "secondary-filled",
            },
            standard: {
                "lodestar-light": "secondary",
                "ursa-light": "secondary-filled",
            },
        } as const,
    },
    dateRangePickerInputTextSeparatorColor: {
        "lodestar-light": lodestarColors.neutralDark30,
        "ursa-light": color("surface--work--text"),
    },
    dayBackgroundColorActive: {
        "lodestar-light": lodestarColors.neutralLight10,
        "ursa-light": ursaColors.neutral75,
    },
    dayBackgroundColorFocused: {
        "lodestar-light": lodestarColors.buttonDefault,
        "ursa-light": "transparent",
    },
    dayBackgroundColorFocusedSelecting: {
        "lodestar-light": lodestarColors.buttonDefault,
        "ursa-light": ursaColors.blue50,
    },
    dayBackgroundColorHover: {
        "lodestar-light": lodestarColors.buttonDefault,
        "ursa-light": ursaColors.neutral60,
    },
    dayBackgroundColorSelected: {
        "lodestar-light": lodestarColors.focusLight10,
        "ursa-light": color("selection--border"),
    },
    dayBackgroundColorToday: {
        "lodestar-light": "transparent",
        "ursa-light": ursaColors.blue25,
    },
    dayBackgroundColorTodayActive: {
        "lodestar-light": lodestarColors.buttonDefault,
        "ursa-light": ursaColors.blue50,
    },
    dayBackgroundColorTodayHover: {
        "lodestar-light": lodestarColors.buttonDefault,
        "ursa-light": ursaColors.blue50,
    },
    dayBorderColorToday: {
        "lodestar-light": toRgba("#000000", 0.4),
        "ursa-light": "transparent",
    },
    dayBorderRadius: {
        "lodestar-light": "2px",
        "ursa-light": border.radius20,
    },
    dayColor: {
        "lodestar-light": lodestarColors.neutralDark30,
        "ursa-light": color("neutral--text"),
    },
    dayColorDisabled: {
        "lodestar-light": lodestarColors.neutral,
        "ursa-light": color("surface--work--text--inactive"),
    },
    dayColorSelected: {
        "lodestar-light": lodestarColors.neutralDark30,
        "ursa-light": ursaColors.blue25,
    },
    dayColorToday: {
        "lodestar-light": lodestarColors.neutralDark30,
        "ursa-light": color("blue--text-alt"),
    },
    dayFocusRingGap: {
        "lodestar-light": 2,
        "ursa-light": 0,
    },
    dayFontWeightSelected: {
        "lodestar-light": 600,
        "ursa-light": typography.fontWeight.bold,
    },
    dayFontWeightToday: {
        "lodestar-light": 600,
        "ursa-light": typography.fontWeight.bold,
    },
    dayLabelColor: {
        "lodestar-light": lodestarColors.neutralDark20,
        "ursa-light": color("surface--work--text--inactive"),
    },
    dayLabelFontSize: {
        "lodestar-light": "14px",
        "ursa-light": typography.fontSize.size20,
    },
    dayMargin: {
        "lodestar-light": "0",
        "ursa-light": `0 ${space.space20}`,
    },
    dayRowGap: {
        "lodestar-light": "0",
        "ursa-light": space.space10,
    },
    dayTransitionDuration: {
        "ursa-light": "75ms",
        "lodestar-light": "0s",
    },
    padding: {
        "lodestar-light": `${sizes.large}px 28px`,
        "ursa-light": space.space40,
    },
    topButtonType: {
        "lodestar-light": "borderless" as const,
        "ursa-light": "secondary-empty" as const,
    },
    dayWidth: {
        "lodestar-light": "40px",
        "ursa-light": space.space60,
    },
    dayInnerPadding: {
        "lodestar-light": "0",
        "ursa-light": "2px",
    },
    dateRangeLimitBorderFlatteningType: {
        "lodestar-light": "button",
        "ursa-light": "background",
    },
    dateRangePickerInRangeBackgroundColor: {
        inProgress: {
            "lodestar-light": lodestarColors.buttonDefault,
            "ursa-light": ursaColors.blue50,
        },
        selected: {
            "lodestar-light": lodestarColors.visBlueLight30,
            "ursa-light": ursaColors.blue50,
        },
    },
    yearSelector: {
        backgroundColor: {
            hover: {
                "lodestar-light": lodestarColors.neutralLight20,
                "ursa-light": ursaColors.neutral60,
            },
            expanded: {
                "lodestar-light": lodestarColors.neutralLight10,
                "ursa-light": ursaColors.neutral75,
            },
        },
    },
});

export function getBackgroundColor({ themeMode }: Pick<DatePickerDialogStyledProps, "themeMode">): string {
    return datePickerTheme.backgroundColor(themeMode);
}

export function getBorderRadius({ themeMode }: Pick<DatePickerDialogStyledProps, "themeMode">): string {
    return datePickerTheme.borderRadius(themeMode);
}

export function getBoxShadow({ themeMode }: Pick<DatePickerDialogStyledProps, "themeMode">): string {
    return datePickerTheme.boxShadow(themeMode);
}

export function getDayBackgroundColorActive({ themeMode }: { themeMode: RequiredThemeKeys }): string {
    return datePickerTheme.dayBackgroundColorActive(themeMode);
}

export function getDayBackgroundColorFocused({ themeMode }: { themeMode: RequiredThemeKeys }): string {
    return datePickerTheme.dayBackgroundColorFocused(themeMode);
}

export function getDayBackgroundColorHover({ themeMode }: { themeMode: RequiredThemeKeys }): string {
    return datePickerTheme.dayBackgroundColorHover(themeMode);
}

export function getDayBackgroundColorSelected({ themeMode }: { themeMode: RequiredThemeKeys }): string {
    return datePickerTheme.dayBackgroundColorSelected(themeMode);
}

export function getDayBackgroundColorToday({ themeMode }: { themeMode: RequiredThemeKeys }): string {
    return datePickerTheme.dayBackgroundColorToday(themeMode);
}

export function getDayBackgroundColorTodayActive({ themeMode }: { themeMode: RequiredThemeKeys }): string {
    return datePickerTheme.dayBackgroundColorTodayActive(themeMode);
}

export function getDayBackgroundColorTodayHover({ themeMode }: { themeMode: RequiredThemeKeys }): string {
    return datePickerTheme.dayBackgroundColorTodayHover(themeMode);
}

export function getDayBorderColorToday({ themeMode }: { themeMode: RequiredThemeKeys }): string {
    return datePickerTheme.dayBorderColorToday(themeMode);
}

export function getDayBorderRadius({ themeMode }: { themeMode: RequiredThemeKeys }): string {
    return datePickerTheme.dayBorderRadius(themeMode);
}

export function getDayColor({ themeMode }: DatePickerDayButtonStyledProps): string {
    return datePickerTheme.dayColor(themeMode);
}

export function getDayColorDisabled({ themeMode }: { themeMode: RequiredThemeKeys }): string {
    return datePickerTheme.dayColorDisabled(themeMode);
}

export function getDayColorSelected({ themeMode }: { themeMode: RequiredThemeKeys }): string {
    return datePickerTheme.dayColorSelected(themeMode);
}

export function getDayColorToday({ themeMode }: { themeMode: RequiredThemeKeys }): string {
    return datePickerTheme.dayColorToday(themeMode);
}

export function getDayFocusRingGap({ themeMode }: { themeMode: RequiredThemeKeys }): number {
    return datePickerTheme.dayFocusRingGap(themeMode);
}

export function getDayFontWeightSelected({ themeMode }: { themeMode: RequiredThemeKeys }): number {
    return datePickerTheme.dayFontWeightSelected(themeMode);
}

export function getDayFontWeightToday({ themeMode }: { themeMode: RequiredThemeKeys }): number {
    return datePickerTheme.dayFontWeightToday(themeMode);
}

export function getDayLabelColor({ themeMode }: DatePickerDayBoxProps): string {
    return datePickerTheme.dayLabelColor(themeMode);
}

export function getDayLabelFontSize({ themeMode }: DatePickerDayBoxProps): string {
    return datePickerTheme.dayLabelFontSize(themeMode);
}

export function getPadding({ themeMode }: DatePickerDialogStyledProps): string {
    return datePickerTheme.padding(themeMode);
}

export function getTypeRampPropsMonthYear(themeMode: RequiredThemeKeys) {
    switch (themeMode) {
        case "ursa-light":
            return {
                system: "ursa",
                variant: "Inter--16px--700w",
            } as const;
        case "lodestar-light":
        case "oldestar":
        default:
            return {
                system: "lodestar",
                variant: "headlineSmall",
            } as const;
    }
}

export function getTypeRampPropsDayOfWeek(themeMode: RequiredThemeKeys) {
    switch (themeMode) {
        case "ursa-light":
            return {
                system: "ursa",
                variant: "Inter--12px--500w",
            } as const;
        case "lodestar-light":
        case "oldestar":
        default:
            return {
                system: "lodestar",
                variant: "headerSmall",
            } as const;
    }
}

export function getTypeRampPropsDay(themeMode: RequiredThemeKeys) {
    switch (themeMode) {
        case "ursa-light":
            return {
                system: "ursa",
                variant: "Inter--14px--400w",
            } as const;
        case "lodestar-light":
        case "oldestar":
        default:
            return {
                system: "lodestar",
                variant: "headerLarge",
            } as const;
    }
}

// implements faux-rounded corners for days adjacent to invalid days, for ursa theme
export function getDisabledBumperStyles({ themeMode }: DatePickerDayBoxProps) {
    switch (themeMode) {
        case "ursa-light":
            return `
                &:before,
                &:after {
                    content: "";
                    width: 10px;
                    background-color: ${datePickerTheme.backgroundColor(themeMode)};
                    height: 32px;
                    position: absolute;
                    top: -2px;
                    mask-repeat: no-repeat;
                    mask-size: contain;
                    z-index: 1;
                    pointer-events: none;
                }
                &:before {
                    left: -14px;
                    mask-image: url("data:image/svg+xml;base64,PHN2ZwogICB3aWR0aD0iMTAiCiAgIGhlaWdodD0iMzIiCiAgIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CiAgPHBhdGgKICAgICBkPSJNIDAsMCBDIDIuMjA5MTM5LDAgNC4yMDg1MzQ4LDAuODk2MDM0NyA1LjY1NjI1LDIuMzQzNzUgNy4xMDM5NjUzLDMuNzkxNDY1MiA4LDUuNzkwODYxIDgsOCBWIDI0IEMgOCwyNi4yMDkxMzkgNy4xMDM5NjUzLDI4LjIwODUzNSA1LjY1NjI1LDI5LjY1NjI1IDQuMjA4NTM0OCwzMS4xMDM5NjUgMi4yMDkxMzksMzIgMCwzMiBjIDMuMzMzMzMzMywwIDYuNjY2NjY2NywwIDEwLDAgViAwIEMgNi4zNDY1MzY3LDAgMy41NTA2NTU0LDAgMCwwIFoiIC8+Cjwvc3ZnPgo=");
                }
                &:after {
                    right: -14px;
                    mask-image: url("data:image/svg+xml;base64,PHN2ZwogICB3aWR0aD0iMTAiCiAgIGhlaWdodD0iMzIiCiAgIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CiAgPHBhdGgKICAgICBkPSJNIDEwLDAgQyA3Ljc5MDg2MSwwIDUuNzkxNDY1MiwwLjg5NjAzNDcgNC4zNDM3NSwyLjM0Mzc1IDIuODk2MDM0NywzLjc5MTQ2NTIgMiw1Ljc5MDg2MSAyLDggdiAxNiBjIDAsMi4yMDkxMzkgMC44OTYwMzQ3LDQuMjA4NTM1IDIuMzQzNzUsNS42NTYyNSBDIDUuNzkxNDY1MiwzMS4xMDM5NjUgNy43OTA4NjEsMzIgMTAsMzIgNi42NjY2NjY3LDMyIDMuMzMzMzMzMywzMiAwLDMyIFYgMCBjIDMuNjUzNDYzMywwIDYuNDQ5MzQ0NiwwIDEwLDAgeiIgLz4KPC9zdmc+Cg==");
                }
            `;
        case "lodestar-light":
        case "oldestar":
        default:
            return "";
    }
}
